/**
 * Auth client — wraps the `/auth/*` routes on `apps/api` plus Better Auth's
 * canonical `/api/auth/get-session`.
 *
 * Architecture refs:
 *   §4.11 — Better Auth on Workers + D1 (routes documented in
 *           apps/api/src/routes/auth/index.ts).
 *   §4.12 — Cookie flags. Session lives in an `HttpOnly; Secure; SameSite=Lax`
 *           cookie set by the server; this client never reads or writes it.
 *   §4.25 — CSRF double-submit. Mutating requests attach the `X-CSRF-Token`
 *           header read from the `__Host-csrf` cookie (see `./csrf.ts`).
 *
 * The client is stateless — all session state lives in the cookie. Callers
 * who need to know the current user call `getSession()` which hits the
 * server and returns the typed session object.
 *
 * Errors: server non-2xx responses reject with `AuthError` carrying the
 * server-supplied `error` code (e.g. `INVALID_INPUT`, `HANDLE_TAKEN`) and
 * `message` where present.
 */

import { getCsrfHeader } from "./csrf.js";

export { getCsrfHeader } from "./csrf.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SignupInput {
  email: string;
  password: string;
  name: string;
  handle: string;
  turnstileToken: string;
}

export interface LoginInput {
  email: string;
  password: string;
  /** Required after 3+ recent failures — the server responds 400 with
   *  `error: "TURNSTILE_REQUIRED"` in that case, callers then retry. */
  turnstileToken?: string;
}

export interface PasswordResetRequestInput {
  email: string;
  turnstileToken: string;
}

export interface PasswordResetConfirmInput {
  token: string;
  newPassword: string;
}

/**
 * Shape of `/api/auth/get-session` (Better Auth). Non-exhaustive — only the
 * fields Track 2.x consumers actually need. Server may add fields over time;
 * keeping this as an `interface` (not a generated zod schema) is deliberate
 * because BA owns the canonical shape.
 */
export interface Session {
  user: {
    id: string;
    email: string;
    name: string;
    emailVerified?: boolean;
  };
  session: {
    id: string;
    expiresAt: string;
  };
}

export class AuthError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "AuthError";
    this.status = status;
    if (code !== undefined) this.code = code;
  }
}

// ─── Client ───────────────────────────────────────────────────────────────────

export interface AuthClientOptions {
  /** API origin, e.g. `https://proofcook.com`. No trailing slash. */
  baseUrl: string;
  /** Override the global fetch — tests + MSW. */
  fetch?: typeof fetch;
}

export interface AuthClient {
  signup(input: SignupInput): Promise<void>;
  login(input: LoginInput): Promise<void>;
  logout(): Promise<void>;
  /** Hit `/auth/verify?token=<token>`. Returns the server response body. */
  verify(token: string): Promise<unknown>;
  requestPasswordReset(input: PasswordResetRequestInput): Promise<void>;
  confirmPasswordReset(input: PasswordResetConfirmInput): Promise<void>;
  /** Returns null if no session (server returns null/empty body). */
  getSession(): Promise<Session | null>;
}

export function createAuthClient(opts: AuthClientOptions): AuthClient {
  const baseUrl = opts.baseUrl.replace(/\/+$/, "");
  const f = opts.fetch ?? globalThis.fetch.bind(globalThis);

  async function mutate(path: string, body: unknown): Promise<Response> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (typeof location !== "undefined" && location.origin) {
      // The server's CSRF / Origin check fires on every mutating call; the
      // browser already attaches Origin automatically on CORS and same-origin
      // requests, but set it explicitly so unit tests (which may run in
      // environments where the browser would otherwise omit it) match prod.
      headers["Origin"] = location.origin;
    }
    const csrf = getCsrfHeader();
    if (csrf) headers["X-CSRF-Token"] = csrf;

    return f(`${baseUrl}${path}`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    });
  }

  async function parseError(resp: Response): Promise<AuthError> {
    let code: string | undefined;
    let msg = `HTTP ${resp.status}`;
    try {
      const body = (await resp.json()) as { error?: string; message?: string };
      if (typeof body.error === "string") code = body.error;
      if (typeof body.message === "string") msg = body.message;
    } catch {
      // Non-JSON body — keep the HTTP default message.
    }
    return new AuthError(resp.status, msg, code);
  }

  async function run(path: string, body: unknown): Promise<void> {
    const resp = await mutate(path, body);
    if (!resp.ok) throw await parseError(resp);
  }

  return {
    signup: (input) => run("/auth/signup", input),
    login: (input) => run("/auth/login", input),
    logout: () => run("/auth/logout", {}),

    async verify(token: string) {
      const resp = await f(`${baseUrl}/auth/verify?token=${encodeURIComponent(token)}`, {
        credentials: "include",
      });
      if (!resp.ok) throw await parseError(resp);
      return resp.json().catch(() => null);
    },

    requestPasswordReset: (input) => run("/auth/password-reset/request", input),
    confirmPasswordReset: (input) => run("/auth/password-reset/confirm", input),

    async getSession(): Promise<Session | null> {
      const resp = await f(`${baseUrl}/api/auth/get-session`, {
        credentials: "include",
      });
      if (resp.status === 401 || resp.status === 404) return null;
      if (!resp.ok) throw await parseError(resp);
      const body = (await resp.json().catch(() => null)) as Session | null | Record<string, never>;
      if (!body || typeof body !== "object" || !("user" in body)) return null;
      return body as Session;
    },
  };
}
